import { blankToNull } from "@/domain/validation";
import type { BusinessProfileInput } from "@/domain/business/schema";
import type { Business } from "@/domain/business/types";

export function businessProfileValues(
  business: Business | null,
  loginEmail: string | null,
): BusinessProfileInput {
  if (!business) {
    return {
      name: "",
      addressStreet: null,
      addressCity: null,
      addressCode: null,
      locationLatitude: null,
      locationLongitude: null,
      phone: null,
      notificationEmail: loginEmail,
      hasDiscountLimit: false,
      discountUsesPerCustomer: null,
      discountUsesPeriodDays: null,
    };
  }

  return {
    name: business.name,
    addressStreet: business.addressStreet,
    addressCity: business.addressCity,
    addressCode: business.addressCode,
    locationLatitude: business.locationLatitude,
    locationLongitude: business.locationLongitude,
    phone: business.phone,
    notificationEmail: business.notificationEmail ?? loginEmail,
    hasDiscountLimit: business.discountUsesPerCustomer !== null,
    discountUsesPerCustomer: business.discountUsesPerCustomer,
    discountUsesPeriodDays: business.discountUsesPeriodDays,
  };
}

type BusinessProfileFieldValues = Partial<Record<keyof BusinessProfileInput, unknown>>;

/** An input hands a blank field back as "" and an emptied number one as NaN,
 *  while a saved business holds null; comparing those raw reads an untouched
 *  form as edited. A key the shown values never carried is blank here too —
 *  unlike in a schema, where an absent key is a payload to refuse. */
function comparableValue(value: unknown): unknown {
  if (value === undefined) return null;
  const filledValue = blankToNull(value);
  return typeof filledValue === "string" ? filledValue.trim() : filledValue;
}

function comparedFields(
  shown: BusinessProfileFieldValues,
  current: BusinessProfileFieldValues,
): (keyof BusinessProfileInput)[] {
  const fields = new Set([...Object.keys(shown), ...Object.keys(current)]);
  return [...fields] as (keyof BusinessProfileInput)[];
}

export function hasBusinessProfileEdits(
  shown: BusinessProfileFieldValues | undefined,
  current: BusinessProfileFieldValues,
): boolean {
  if (!shown) return false;
  return comparedFields(shown, current).some(
    (field) => comparableValue(shown[field]) !== comparableValue(current[field]),
  );
}
